import { createElement, exposeFunctions, updateElement } from "./core.js";

/**
 * Clears the toast element obtained from toast().
 * @param {HTMLElement} item
 */
export function removeToast(item) {
	if (!item || !item.isConnected)
		return;

	item.style.opacity = '0';
	delete item.dataset.timeoutId;

	// allow time for fade-out animation
	setTimeout(() => item.remove(), 300);
}

function getToastContainer() {
	const id = "toast-container";
	// Create toast container if it doesn't exist
	let toastContainer = document.getElementById(id);
	if (!toastContainer) {
		toastContainer = document.createElement('div');
		toastContainer.id = id;
		document.body.appendChild(toastContainer);
	}
	return toastContainer;
}

/**
 * Shows a toast message on the screen.
 * @param {string} message
 * @param {number} [duration] In seconds, defaults to 3 seconds
 * @returns {HTMLElement} The toast element
 */
export function toast(message, duration) {
	return updateToast(null, message, duration);
}

/**
 * Updates a toast message on the screen.
 * Shows a new one if the provided one is not found.
 * If duration is 0 then the toast is removed instead.
 * @param {HTMLElement} item
 * @param {string} message
 * @param {number} [duration] In seconds, defaults to 3 seconds
 * @returns {HTMLElement} The toast element
 */
export function updateToast(item, message, duration) {
	if (!item || !item.isConnected) {
		// Create toast element

		item = createElement({
			tag: 'div', class: 'toast-message',
			content: [{ tag: 'span', text: message }],
		});

		// Add to container
		getToastContainer().appendChild(item);

		// Show to user with animation
		setTimeout(() => item.style.opacity = '1', 10);
	}
	else {
		const currentText = item.firstElementChild.dataset.i18nText;
		if (currentText && currentText != message) {
			updateElement(item, { content: [{ tag: 'span', text: message }] });
		}
		clearTimeout(item.dataset.timeoutId);
	}

	// Auto-dismiss after timeout
	const timeout = (duration ?? 3) * 1000;

	const tid = setTimeout(removeToast, timeout, item);
	item.dataset.timeoutId = tid.toString();

	return item;
}

/**
 * Shows a busy indicator as a toast.
 * @param {number} [duration] In seconds, defaults to 10 minutes
 * @returns {HTMLElement} The toast element
 */
export function newBusyToast(duration) {
	const item = createElement({
		tag: "div", class: "busy-indicator",
		content: [{
			tag: "span",
			class: "spinner-border spinner-border-sm",
			role: "status",
			attri: { "aria-hidden": "true" }
		}]
	});

	// Add to container
	getToastContainer().appendChild(item);

	// Wait a second before showing to user
	item.hidden = true;
	setTimeout(() => item.hidden = false, 1000);

	// Auto-dismiss after timeout
	const timeout = (duration ?? 600) * 1000;
	setTimeout(removeToast, timeout, item);

	return item;
}

export default toast;

exposeFunctions({ toast, updateToast, removeToast, newBusyToast });

